import type { ReactNode } from 'react';
import type { Pipeline, PipelineItem } from '@orgus/protocol';
import { Badge, Pressable, RowStatus, cn } from '@orgus/ui';
import type { RowStatusProps } from '@orgus/ui';
import { stageTitle } from './model.ts';
import { failedLabel } from './present.ts';

/** Column widths shared by the column labels and every row, so the numbers line up. */
export const RESULT_COL = { status: 'w-4', first: 'w-16', last: 'w-16 sm:w-20' } as const;

/** A finished item's status, by its colour and icon: landed green, failed red, cancelled grey. */
export function resultStatus(i: Pick<PipelineItem, 'state' | 'stage'>, p: Pipeline): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  if (i.state === 'passed') return { tone: 'success', icon: 'done', label: p.stages[p.stages.length - 1]?.title ?? 'Passed' };
  if (i.state === 'failed') return { tone: 'danger', icon: 'failed', label: failedLabel(stageTitle(p, i.stage)) };
  if (i.state === 'cancelled') return { tone: 'neutral', icon: 'stopped', label: 'Cancelled' };
  if (i.state === 'active') return { tone: 'working', icon: 'working', label: 'In progress' };
  return { tone: 'neutral', icon: 'queued', label: 'Queued' };
}

/** The conventional-commit type and scope ("feat(memory)"), as the row's one small mono chip. */
export function ScopeChip({ type }: { type: string | null }) {
  if (!type) return null;
  return (
    <Badge data-chip="" tone="neutral" size="sm" className="hidden max-w-40 shrink-0 font-mono lg:inline-flex" title={type}>
      {type}
    </Badge>
  );
}

/**
 * The row budget on a detail line (at most two meta facts, a failure's short status counting as
 * one): "Attempt 2, Conflict in approvals.ts, land-c9b" after "Verify failed" reads "Conflict in
 * approvals.ts". The attempt is the Resubmitted view's and the drawer's to say; the rest is the
 * line's tooltip.
 */
export function rowFacts(detail: string, failure?: string): string {
  const facts = detail.split(/,\s+/).filter(Boolean);
  if (failure && facts.length > 1 && /^attempt \d+$/i.test(facts[0]!)) facts.shift();
  return facts.slice(0, failure ? 1 : 2).join(', ');
}

/**
 * One finished item or chain, one line, in the session rows' anatomy: its status as a tinted
 * icon in a fixed column (the label is its tooltip and accessible name), the subject in text-ui
 * medium cut short, the scope as one small mono chip, the detail as a dim caption, then two
 * right-aligned numeric columns.
 */
export function ResultRow({
  status,
  title,
  fullTitle,
  type,
  detail,
  failure,
  first,
  firstTitle,
  last,
  onOpen,
}: {
  status: Pick<RowStatusProps, 'tone' | 'icon' | 'label'>;
  title: string;
  fullTitle: string;
  type: string | null;
  detail: string;
  /** A failed item's short status ("Checks failed"), before the detail in the danger ink. */
  failure?: string;
  first: ReactNode;
  firstTitle?: string;
  last: ReactNode;
  onOpen: () => void;
}) {
  return (
    <li className="motion-arrive">
      {/* On a phone the row takes two lines: the subject on the first (two lines rather than cut
          after a few words), its status, cause and time on the second, so two attempts of one
          change read apart. The inset shrinks to the gutter's step, as the Now rows do. */}
      <Pressable
        data-row=""
        onClick={onOpen}
        className="flex h-row w-full items-center gap-3 rounded-control px-row-x transition-colors duration-(--duration-fast) hover:bg-hover max-sm:h-auto max-sm:min-h-row-2line max-sm:items-start max-sm:px-2 max-sm:py-2"
      >
        <span className={cn(RESULT_COL.status, 'flex shrink-0 justify-center max-sm:h-5 max-sm:items-center')}>
          <RowStatus {...status} hideLabel focusable={false} />
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-3 max-sm:flex-col max-sm:items-stretch max-sm:gap-1">
          <span className="min-w-0 truncate text-ui font-medium text-fg max-sm:line-clamp-2 max-sm:whitespace-normal sm:max-w-3/5" title={fullTitle}>
            {title}
          </span>
          <ScopeChip type={type} />
          {(detail || failure) && (
            <span data-meta="" className="flex min-w-0 flex-1 items-center gap-2 text-caption text-fg-subtle">
              {failure && <span className="shrink-0 font-medium text-status-danger-ink">{failure}</span>}
              {detail && (
                <span className="min-w-0 truncate" title={detail}>
                  {rowFacts(detail, failure)}
                </span>
              )}
              <span className="ml-auto shrink-0 text-caption text-fg-subtle num sm:hidden">{last}</span>
            </span>
          )}
          {!(detail || failure) && <span className="text-caption text-fg-subtle num sm:hidden">{last}</span>}
        </span>
        <span className={cn(RESULT_COL.first, 'hidden shrink-0 truncate text-right text-ui text-fg-muted num sm:block')} title={firstTitle}>
          {first}
        </span>
        <span className={cn(RESULT_COL.last, 'shrink-0 truncate text-right text-caption text-fg-subtle num max-sm:hidden')}>{last}</span>
      </Pressable>
    </li>
  );
}

/** The tiny labels over a result list's numeric columns. */
export function ResultColumns({ first, last }: { first: string; last: string }) {
  const head = 'shrink-0 truncate text-right text-caption text-fg-subtle';
  return (
    <div className="hidden h-8 items-center gap-3 px-row-x sm:flex" aria-hidden>
      <span className={cn(RESULT_COL.status, 'shrink-0')} />
      <span className="min-w-0 flex-1" />
      <span className={cn(RESULT_COL.first, head)}>{first}</span>
      <span className={cn(RESULT_COL.last, head)}>{last}</span>
    </div>
  );
}
